import Notes from "@/components/notes";
import { Button } from "@msuite/ui/button";
import { getFormErrors } from "@msuite/ui/form";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Panels } from "@msuite/ui/panels";
import { Tabs, type TabPanelGroup } from "@msuite/ui/tabs";
import { Tooltip } from "@msuite/ui/tooltip";
import { cn } from "@msuite/ui/utils";
import type { CalculationFormSchema } from "@msuite/utils/schemas";
import { useCalculationEditorContext } from "@/sites/calculations/calculation-editor/context";
import { Fragment, useState } from "react";
import { useFormContext } from "react-hook-form";
import { CalculationFormFiles } from "./calculation-form-files";
import { CalculationFormFooter } from "./calculation-form-footer";
import { CalculationFormItems } from "./calculation-form-items";
import { CalculationFormMembers } from "./calculation-form-members";
import { CalculationFormMeta } from "./calculation-form-meta";
import { CalculationFormMetaData } from "./calculation-form-meta-data";
import { CalculationFormServices } from "./calculation-form-services";
import { CalculationFormContext } from "./context";

export function CalculationForm() {
	/** Context */
	const { calculationId, isLocked } = useCalculationEditorContext();

	/** State */
	const [isEditing, setIsEditing] = useState<string[]>([]);
	const methods = useFormContext<CalculationFormSchema>();
	const calculationType = methods.getValues("type");

	/** Hooks */
	const disclosure = useDisclosure({ defaultIsOpen: true });
	const { t } = useTranslation();

	/** Computed */
	const groups: Array<TabPanelGroup | null> = [
		{
			name: "all",
			panels: [
				{
					label: t("organization.data.services"),
					panel: () => <CalculationFormServices />,
					errors: getFormErrors(methods.formState.errors, ["services", "name"]),
					cyId: "calculation-form-services-tab",
				},
			],
		},
		calculationType !== "parsc"
			? {
					name: "meta",
					panels: [
						{
							label: t("calculations.meta_data"),
							panel: () => (
								<fieldset
									disabled={isLocked}
									className="contents"
								>
									<CalculationFormMetaData />
								</fieldset>
							),
							errors: getFormErrors(methods.formState.errors, [
								"offerNumber",
								"projectName",
								"clientId",
								"streetName",
								"houseNumber",
								"zipCode",
								"city",
							]),
							cyId: "calculation-form-meta-tab",
						},
					],
				}
			: null,
		{
			name: "members",
			panels: [
				{
					label: t("organization.members"),
					panel: () => (
						<fieldset
							disabled={isLocked}
							className="contents"
						>
							<CalculationFormMembers calculationId={calculationId} />
						</fieldset>
					),
					errors: [],
					cyId: "calculation-form-members-tab",
				},
			],
		},
		{
			name: "misc",
			panels: [
				{
					label: t("notes.notes"),
					panel: () => (
						<Notes
							notesTable="serviceCalculationNotes"
							identifier={calculationId}
						/>
					),
					errors: [],
					cyId: "calculation-form-notes-tab",
				},
				{
					label: t("files.files"),
					panel: () => (
						<fieldset
							disabled={isLocked}
							className="contents"
						>
							<CalculationFormFiles />
						</fieldset>
					),
					errors: [],
					cyId: "calculation-form-files-tab",
				},
			],
		},
	];

	/** Render */
	return (
		<CalculationFormContext.Provider value={{ isEditing, setIsEditing }}>
			<Panels className="relative">
				{!disclosure.isOpen && (
					<div className="absolute right-0 top-0">
						<Tooltip label={t("calculations.expand_sidebar")}>
							<Button
								onClick={disclosure.onToggle}
								icon={<Icon name="PanelLeftClose" />}
							/>
						</Tooltip>
					</div>
				)}
				<Panels.Panel
					flex={1.5}
					className={cn(
						"relative v-stack overflow-hidden min-h-0",
						!disclosure.isOpen && "pr-[20px]",
					)}
				>
					<fieldset
						disabled={isLocked}
						className="contents"
					>
						<CalculationFormMeta />
						<CalculationFormItems />
					</fieldset>
				</Panels.Panel>
				<Panels.Panel
					flex={disclosure.isOpen ? 1 : 0}
					className="bottom-space min-h-0 flex flex-col h-full overflow-hidden!"
				>
					{disclosure.isOpen && (
						<Fragment>
							<div className="h-full flex-1 flex">
								<Tabs
									className="h-full flex-1 flex"
									groups={groups.filter((group): group is TabPanelGroup => group !== null)}
									withHeader
									header={() => (
										<Tooltip label={t("calculations.collapse_sidebar")}>
											<Button
												onClick={disclosure.onToggle}
												icon={<Icon name="PanelRightClose" />}
											/>
										</Tooltip>
									)}
								/>
							</div>
							<CalculationFormFooter />
						</Fragment>
					)}
				</Panels.Panel>
			</Panels>
		</CalculationFormContext.Provider>
	);
}
